import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PrimeReactSingleSelect from '../../atoms/single-select-dropdown/single-select-dropdown';
import { Sphere } from 'types/global';
import type { FilterExpression } from 'types/filters';
import { useFetchPotentialAssignees, OptionType } from '@center-platform/spheres/identity';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import MultiSelectPerspectiveSelector from '../PerspectiveMultiSelector/MultiSelectPerspectiveSelector';
import { Tooltip } from 'primereact/tooltip';
import { useApiClients } from '../../../context/ApiClientsContext';
import './PerspectiveSelector.scss';
interface PerspectiveSelectorProps {
  perspectiveSystemName: string;
  sphere: Sphere;
  organizationId: string;
  onAssigneeSelected?: (
    assignee: string | null,
    sphereId: string,
    perspectiveSystemName: string,
    assigneeSphere?: Sphere
  ) => void;
  onAssigneesSelected?: (
    assignees: string[] | null,
    sphereId: string,
    perspectiveSystemName: string,
    assigneeSpheres?: Sphere[]
  ) => void;
  onDone?: (
    sphereId: string,
    perspectiveSystemName: string,
    assignees: string[]
  ) => void;
  showLabel?: boolean;
  label?: React.ReactNode;
  isEditing?: boolean;
  stayOpen?: boolean;
  className?: string;
  isAtlas?: boolean;
  nearbySphereId?: string;
  multi?: boolean;
  contextFilters?: FilterExpression[] | null;
  /** When false, eagerly fetch on mount. Default true — data loads on first dropdown open. */
  lazy?: boolean;
  /**
   * Whether the caller may open the assign dropdown from this control. Default `true`
   * for non-Atlas call sites, which predate this gate. Atlas callers pass the resolved
   * `moleculeProps.assignable && useSpherePermission(sphere.id, 'edit')` value — the
   * server re-checks `edit` on every assignment regardless of this UI-only gate.
   */
  assignable?: boolean;
  /** Which parts of the assigned sphere render in the read (non-editing) state. */
  display?: 'avatar+name' | 'avatar' | 'name';
  /** Text shown in place of the avatar+name when no sphere is assigned. */
  placeholder?: string;
  /** Caps the avatar stack when `multi` — forwarded to `PerspectiveAssigneesDisplayAtom`. */
  maxAvatars?: number;
}

const PerspectiveSelector: React.FC<PerspectiveSelectorProps> = React.memo(({
  perspectiveSystemName,
  organizationId,
  sphere,
  onAssigneeSelected,
  onAssigneesSelected,
  onDone,
  showLabel = false,
  label = '',
  isEditing = false,
  stayOpen = false,
  className = '',
  isAtlas = false,
  nearbySphereId = null,
  multi = false,
  contextFilters,
  lazy = true,
  assignable = true,
  display = 'avatar+name',
  placeholder: emptyPlaceholder = '',
  maxAvatars,
}) => {
  const { searchClient } = useApiClients();
  const nodeRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<any>(null);
  const [nearbyMode, setNearbyMode] = useState<boolean>(false);

  const nearbyModeInitialized = useRef(false);
  useEffect(() => {
    if (!nearbyModeInitialized.current) {
      nearbyModeInitialized.current = true;
      return;
    }
    if (isEditingLocal) {
      fetchData('replace');
    }
  }, [nearbyMode]);

  let initialSelectedOption = null;
  let assignedSphere = null;
  let allAssignedSpheres: Sphere[] = [];

  if (sphere?.assignedPerspectives) {
    // Handle both context API format (perspectiveSystemName, assignedSpheres[])
    // and legacy format (perspectiveProperties.system_name, assigned[].sphere)
    const assignedPerspective = sphere.assignedPerspectives.find(
      (p: any) => p?.perspectiveSystemName === perspectiveSystemName || p?.perspectiveProperties?.system_name === perspectiveSystemName
    );

    // Handle both context API format (assignedSpheres[]) and legacy format (assigned[].sphere)
    const ap = assignedPerspective as any; // eslint-disable-line @typescript-eslint/no-explicit-any -- handles dual API formats
    const rawAssigned = ap?.assignedSpheres ?? ap?.assigned?.map((a: any) => a.sphere) ?? [];
    if (assignedPerspective && rawAssigned.length > 0) {
      allAssignedSpheres = rawAssigned;
      assignedSphere = allAssignedSpheres[0];

      let assignedSphereImageId = null;
      if (assignedSphere?.properties.imageId) {
        if (Array.isArray(assignedSphere.properties.imageId)) {
          assignedSphereImageId = assignedSphere.properties.imageId[0];
        } else {
          assignedSphereImageId = assignedSphere.properties.imageId;
        }
      }

      if (assignedSphere) {
        initialSelectedOption = {
          glyph: assignedSphere.glyph,
          glyphColor: assignedSphere.glyphColor,
          name: assignedSphere.properties.name,
          code: assignedSphere.id,
          imageId: assignedSphereImageId,
        };
      }
    }
  }

  const {
    options,
    isLoading,
    hasMore,
    fetchData,
    resetData,
    setSearchTerm,
    triggerInitialFetch,
  } = useFetchPotentialAssignees({
    organizationId,
    schemaSystemName: sphere?.properties?.type || '',
    perspectiveSystemName,
    nearbyMode,
    nearbySphereId,
    selectedOption: initialSelectedOption,
    apiClient: searchClient,
    limit: 20,
    contextFilters,
    lazy,
  });

  const [selectedOption, setSelectedOption] = useState<OptionType | null>(
    initialSelectedOption
  );
  const [isEditingLocal, setIsEditingLocal] = useState(isEditing || stayOpen);
  const [localFilter, setLocalFilter] = useState<string>('');

  // Derive current assigned sphere ID from props for comparison
  const currentAssignedId = useMemo(() => {
    if (!sphere?.assignedPerspectives) {
      return null;
    }
    // Handle both context API format (perspectiveSystemName) and legacy format (perspectiveProperties.system_name)
    const assignedPerspective = sphere.assignedPerspectives.find(
      (p: any) => p?.perspectiveSystemName === perspectiveSystemName || p?.perspectiveProperties?.system_name === perspectiveSystemName
    );
    if (!assignedPerspective) return null;
    // Handle both context API format (assignedSpheres[]) and legacy format (assigned[].sphere)
    const ap = assignedPerspective as any; // eslint-disable-line @typescript-eslint/no-explicit-any -- handles dual API formats
    const assignedId = ap?.assignedSpheres?.[0]?.id ?? ap?.assigned?.[0]?.sphere?.id ?? null;
    return assignedId;
  }, [sphere?.assignedPerspectives, perspectiveSystemName]);

  // Sync selectedOption when the assigned ID changes
  useEffect(() => {
    // If current selection matches the assigned ID, do nothing
    if (selectedOption?.code === currentAssignedId) {
      return;
    }

    // Only update if we have assignedPerspectives data (not during loading)
    if (!sphere?.assignedPerspectives) {
      return;
    }

    // If there's an assigned ID, update selection to match
    if (currentAssignedId) {
      // Handle both context API format and legacy format
      const assignedPerspective = sphere.assignedPerspectives.find(
        (p: any) => p?.perspectiveSystemName === perspectiveSystemName || p?.perspectiveProperties?.system_name === perspectiveSystemName
      );
      if (!assignedPerspective) return;

      // Get the assigned sphere from either format
      const ap = assignedPerspective as any; // eslint-disable-line @typescript-eslint/no-explicit-any -- handles dual API formats
      const assignedSphereData = ap?.assignedSpheres?.[0] ?? ap?.assigned?.[0]?.sphere;

      if (assignedSphereData) {
        let assignedSphereImageId = null;
        if (assignedSphereData?.properties?.imageId) {
          assignedSphereImageId = Array.isArray(assignedSphereData.properties.imageId)
            ? assignedSphereData.properties.imageId[0]
            : assignedSphereData.properties.imageId;
        }
        const newOption = {
          glyph: assignedSphereData.glyph,
          glyphColor: assignedSphereData.glyphColor,
          name: assignedSphereData.properties?.name,
          code: assignedSphereData.id,
          imageId: assignedSphereImageId,
        };
        setSelectedOption(newOption);
      }
    } else {
      // Only clear if we confirmed there's truly no assignment for this perspective
      const assignedPerspective = sphere.assignedPerspectives.find(
        (p: any) => p?.perspectiveSystemName === perspectiveSystemName || p?.perspectiveProperties?.system_name === perspectiveSystemName
      );
      if (!assignedPerspective) {
        setSelectedOption(null);
        return;
      }
      // Check both formats for empty assignments
      const ap = assignedPerspective as any; // eslint-disable-line @typescript-eslint/no-explicit-any -- handles dual API formats
      const hasAssignments = (ap?.assignedSpheres?.length > 0) || (ap?.assigned?.length > 0);
      if (!hasAssignments) {
        setSelectedOption(null);
      }
    }
  }, [currentAssignedId, sphere?.assignedPerspectives, perspectiveSystemName]);

  // Store current values in refs so the callback can access them without recreating
  const optionsLengthRef = useRef(options.length);
  const isLoadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);
  const fetchDataRef = useRef(fetchData);

  useEffect(() => {
    optionsLengthRef.current = options.length;
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
    fetchDataRef.current = fetchData;
  }, [options.length, isLoading, hasMore, fetchData]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(localFilter);
    }, 750);
    return () => clearTimeout(delayDebounce);
  }, [localFilter, setSearchTerm]);

  const BUFFER_OFFSET = 10;
  // Stable callback that uses refs
  const handleLazyLoad = useCallback(async (event: any) => {
    if (event?.first !== undefined && event?.last !== undefined) {
      if (
        event.last + BUFFER_OFFSET > optionsLengthRef.current &&
        !isLoadingRef.current &&
        hasMoreRef.current
      ) {
        try {
          await fetchDataRef.current();
        } catch (err) {
          console.error('[VirtualScroller] fetchData() error:', err);
        }
      }
    } else {
      console.warn(
        '[VirtualScroller] onLazyLoad: event missing first/last; skipping fetchData().'
      );
    }
  }, []); // Empty deps - function never recreated

  const virtualScrollerOptions = useMemo(() => ({
    itemSize: 44,
    lazy: true,
    showLoader: true,
    onLazyLoad: handleLazyLoad,
    delay: 0,
  }), [handleLazyLoad]);

  // Ensure selected option is in the options array for the dropdown
  const optionsWithSelected = useMemo(() => {
    if (!selectedOption) return options;

    // Check if the selected option is already in the options
    const isInOptions = options.some(opt => opt.code === selectedOption.code);

    if (isInOptions) {
      return options;
    }

    // If not, prepend it to ensure the dropdown can display it
    return [selectedOption, ...options];
  }, [options, selectedOption, perspectiveSystemName]);

  const defaultOption = useMemo(() => ({ name: '', code: '' }), []);
  const placeholder = 'Select Assignee';

  const handleChange = (e: { value: OptionType | null }) => {
    if (e.value) {
      setSelectedOption(e.value);
      // Construct a partial Sphere object from the selected option
      const assignedSphere = {
        id: e.value.code,
        properties: {
          name: e.value.name,
          imageId: e.value.imageId,
        },
        glyph: e.value.glyph || '',
        glyphColor: e.value.glyphColor,
        label: e.value.name,
      } as unknown as Sphere;
      setIsEditingLocal(false);
      onAssigneeSelected?.(
        e.value.code,
        sphere.id,
        perspectiveSystemName,
        assignedSphere
      );
    } else {
      onAssigneeSelected?.(null, sphere.id, perspectiveSystemName);
    }
  };

  const handleNearbyModeChange = (e: { nearbyMode: boolean }) => {
    setNearbyMode(e.nearbyMode);
  };

  const handleDropdownOpen = useCallback(() => {
    triggerInitialFetch();
  }, [triggerInitialFetch]);

  const handleDoubleClick = async () => {
    // Don't allow toggling if stayOpen is true - component should always stay open
    if (stayOpen) return;

    // Read-only: the column's `assignable` setting is off, or the caller lacks edit
    // rights on this sphere (or that check hasn't resolved yet — a pending check is
    // treated as denied, never as allowed).
    if (!assignable) return;

    if (!isEditingLocal && (isAtlas || isEditing)) {
      // Hide tooltip immediately on interaction
      if (tooltipRef.current) {
        tooltipRef.current.hide();
      }
      setIsEditingLocal(true);
      setNearbyMode(false);
      fetchData('replace');
    }
  };

  useEffect(() => {
    // Don't set up click-outside listener if stayOpen is true
    if (stayOpen) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is inside the dropdown overlay panel
      const isInsideDropdownPanel = (target as HTMLElement).closest('.p-dropdown-panel');

      if (
        isEditingLocal &&
        nodeRef.current &&
        !nodeRef.current.contains(target) &&
        !isInsideDropdownPanel
      ) {
        setIsEditingLocal(false);
      }
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [isEditingLocal, stayOpen]);
  
  

  const onFilterChange = (e: { filter: string }) => {
    const filterValue = e.filter;
    setLocalFilter(filterValue);
  };

  useEffect(() => {
    if (isEditingLocal && dropdownRef.current) {
      dropdownRef.current.focus();
    }
  }, [isEditingLocal]);

  // Guard against undefined sphere - must be after all hooks
  if (!sphere) {
    return null;
  }

  return multi ? (
    <MultiSelectPerspectiveSelector
      perspectiveSystemName={perspectiveSystemName}
      sphere={sphere}
      organizationId={organizationId}
      onAssigneesSelected={onAssigneesSelected}
      onDone={onDone}
      showLabel={showLabel}
      label={label}
      isEditing={isEditing}
      stayOpen={stayOpen}
      className={className}
      isAtlas={isAtlas}
      nearbySphereId={nearbySphereId ?? undefined}
      contextFilters={contextFilters}
      lazy={lazy}
      maxVisible={maxAvatars}
      placeholder={emptyPlaceholder || undefined}
      assignable={assignable}
    />
  ) : (
    <div
      ref={nodeRef}
      className={`node-perspective-selector align-items-center d-flex w-full ${
        isEditingLocal ? 'editing' : ''
      }${assignable ? '' : ' node-perspective-selector--read-only'}`}
      onDoubleClick={handleDoubleClick}
    >
      {showLabel && (
        <label className="perspective-label">
          {label || sphere.properties.name}
        </label>
      )}
      {isEditingLocal || stayOpen ? (
        <div
          className="perspective-select-container"
          ref={dropdownRef}
        >
          <PrimeReactSingleSelect
            key={`${sphere.id}-${perspectiveSystemName}`}
            value={selectedOption || defaultOption}
            options={optionsWithSelected}
            onChange={handleChange}
            onFilter={onFilterChange}
            onShow={handleDropdownOpen}
            placeholder={placeholder}
            optionLabel="name"
            filter
            filterBy="name"
            showFilterClear
            virtualScrollerOptions={optionsWithSelected.length > 0 ? virtualScrollerOptions : undefined}
            loading={isLoading}
            scrollHeight="300px"
            className={className}
            showNearbyToggle={true}
            onNearbyModeToggled={handleNearbyModeChange}
            resetFilterOnHide={true}
          />
        </div>
      ) : (
        <>
          {allAssignedSpheres.length >= 1 ? (
            <>
              {display !== 'name' && (
                <SphereAvatar
                  glyph={selectedOption?.glyph}
                  glyphColor={selectedOption?.glyphColor}
                  sphereId={selectedOption?.code}
                  sphereName={selectedOption?.name}
                  imageId={selectedOption?.imageId}
                  size="small"
                  className="sphere-avatar-icon"
                />
              )}
              {display !== 'avatar' && (
                <span
                  className={`white-space-nowrap overflow-hidden text-overflow-ellipsis mt-2 ml-1 perspective-selector-label-${sphere.id}-${perspectiveSystemName}`}
                  style={{ width: '100%', fontSize: '12px' }}
                  data-pr-tooltip={selectedOption?.name || ''}
                  data-pr-position="top"
                >
                  {selectedOption?.name ?? '\u00A0'}
                </span>
              )}
              <Tooltip
                ref={tooltipRef}
                target={`.perspective-selector-label-${sphere.id}-${perspectiveSystemName}`}
                showDelay={1000}
                disabled={isEditingLocal}
              />
            </>
          ) : (
            emptyPlaceholder && (
              <span className="perspective-selector-placeholder">{emptyPlaceholder}</span>
            )
          )}
        </>
      )}
    </div>
  );
});

PerspectiveSelector.displayName = 'PerspectiveSelector';

export default PerspectiveSelector;